import React from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Dispatch } from '@reduxjs/toolkit';
import { setSettings } from '../../../../support/slices/tileBuilderSlice';
import { TileSettings } from '../../../../types/tileBuilderTypes';
import { TileDisplayContext } from '../../../../support/tileDisplayContextUtils';
import { ensureNumber } from '../../../../support/utils';
import { setEffectiveMargins } from '../TilePreview/tilePreviewHelpers';

interface LayoutLegendControlsProps {
  chartId: string;
  settings: TileSettings;
  dispatch: Dispatch;
  displayContext: TileDisplayContext;
  legendShown: boolean;
  layoutStrategy: 'auto' | 'manual';
  effectiveMargins: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  leaderLayout?: {
    chartAreaWidth: number;
    chartAreaHeight: number;
  };
}

const LayoutLegendControls: React.FC<LayoutLegendControlsProps> = ({
                                                                     chartId,
                                                                     settings,
                                                                     dispatch,
                                                                     displayContext,
                                                                     legendShown,
                                                                     layoutStrategy,
                                                                     effectiveMargins,
                                                                     leaderLayout,
                                                                   }) => {
  const normalizedMargins = {
    top: ensureNumber(effectiveMargins.top, 24),
    right: ensureNumber(effectiveMargins.right, 16),
    bottom: ensureNumber(effectiveMargins.bottom, 48),
    left: ensureNumber(effectiveMargins.left, 16),
  };

  return (
    <Box>
      {displayContext === 'leader' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" component="div">
            <strong>Leader Context Tips:</strong>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Preview shows exact chart rendering area size</li>
              <li>Adjust margins to optimize for the calculated dimensions above</li>
              <li>Legend is supported in Leader preview and should be tested there</li>
              <li>Test with different strip counts and container heights</li>
            </ul>
          </Typography>
        </Alert>
      )}

      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
        <FormControlLabel
          control={
            <Switch
              checked={!!settings.show_legend}
              onChange={(e) =>
                dispatch(
                  setSettings({
                    chartId,
                    settings: { show_legend: e.target.checked },
                  })
                )
              }
            />
          }
          label="Show Legend"
        />

        <FormControl sx={{ minWidth: 220 }} disabled={!legendShown}>
          <InputLabel>Legend Anchor</InputLabel>
          <Select
            label="Legend Anchor"
            value={settings.legendAnchor ?? 'bottom'}
            onChange={(e) =>
              dispatch(
                setSettings({
                  chartId,
                  settings: {
                    legendAnchor: e.target.value as typeof settings.legendAnchor,
                  },
                })
              )
            }
          >
            <MenuItem value="bottom">Bottom</MenuItem>
            <MenuItem value="left">Left</MenuItem>
            <MenuItem value="right">Right</MenuItem>
            <MenuItem value="top">Top</MenuItem>
            <MenuItem value="top-left">Top-Left</MenuItem>
            <MenuItem value="top-right">Top-Right</MenuItem>
            <MenuItem value="bottom-left">Bottom-Left</MenuItem>
            <MenuItem value="bottom-right">Bottom-Right</MenuItem>
            <MenuItem value="center">Center</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 220 }} disabled={!legendShown}>
          <InputLabel>Legend Direction</InputLabel>
          <Select
            label="Legend Direction"
            value={settings.legendDirection ?? 'row'}
            onChange={(e) =>
              dispatch(
                setSettings({
                  chartId,
                  settings: {
                    legendDirection: e.target.value as typeof settings.legendDirection,
                  },
                })
              )
            }
          >
            <MenuItem value="row">Row</MenuItem>
            <MenuItem value="column">Column</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Legend Translate X"
          type="number"
          value={settings.legendTranslateX ?? 0}
          disabled={!legendShown}
          onChange={(e) =>
            dispatch(
              setSettings({
                chartId,
                settings: { legendTranslateX: Number(e.target.value) },
              })
            )
          }
          sx={{ width: 180 }}
        />

        <TextField
          label="Legend Translate Y"
          type="number"
          value={settings.legendTranslateY ?? 0}
          disabled={!legendShown}
          onChange={(e) =>
            dispatch(
              setSettings({
                chartId,
                settings: { legendTranslateY: Number(e.target.value) },
              })
            )
          }
          sx={{ width: 180 }}
        />

        <TextField
          label="Legend Item Width"
          type="number"
          value={settings.legendItemWidth ?? 104}
          disabled={!legendShown}
          onChange={(e) =>
            dispatch(
              setSettings({
                chartId,
                settings: { legendItemWidth: Number(e.target.value) },
              })
            )
          }
          sx={{ width: 180 }}
        />

        <TextField
          label="Legend Item Height"
          type="number"
          value={settings.legendItemHeight ?? 20}
          disabled={!legendShown}
          onChange={(e) =>
            dispatch(
              setSettings({
                chartId,
                settings: { legendItemHeight: Number(e.target.value) },
              })
            )
          }
          sx={{ width: 180 }}
        />

        <TextField
          label="Legend Symbol Size"
          type="number"
          value={settings.legendSymbolSize ?? 18}
          disabled={!legendShown}
          onChange={(e) =>
            dispatch(
              setSettings({
                chartId,
                settings: { legendSymbolSize: Number(e.target.value) },
              })
            )
          }
          sx={{ width: 180 }}
        />

        <TextField
          label="Legend Items Spacing"
          type="number"
          value={settings.legendItemsSpacing ?? 5}
          disabled={!legendShown}
          onChange={(e) =>
            dispatch(
              setSettings({
                chartId,
                settings: { legendItemsSpacing: Number(e.target.value) },
              })
            )
          }
          sx={{ width: 180 }}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel>Layout Strategy</InputLabel>
          <Select
            label="Layout Strategy"
            value={layoutStrategy}
            onChange={(e) =>
              dispatch(
                setSettings({
                  chartId,
                  settings: {
                    ...settings,
                    layoutStrategy: e.target.value as 'auto' | 'manual',
                  } as any,
                })
              )
            }
          >
            <MenuItem value="auto">Auto (chart computes margins)</MenuItem>
            <MenuItem value="manual">Manual (use margins below)</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="caption" color="text.secondary">
          Margins below are saved separately for legend shown vs hidden and mirrored into
          marginTop/Right/Bottom/Left.
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Margins (
        {displayContext === 'leader'
          ? 'Leader context'
          : legendShown
            ? 'Legend shown'
            : 'Legend hidden'}
        )
        {displayContext === 'leader' && leaderLayout && (
          <Typography
            variant="caption"
            display="block"
            color="primary.main"
            sx={{ mt: 0.5, fontWeight: 600 }}
          >
            Chart renders in {leaderLayout.chartAreaWidth}×{leaderLayout.chartAreaHeight}
            px area. Recommended margins: Left=30-40, Right=10-15, Top=10-15,
            Bottom=25-35
          </Typography>
        )}
      </Typography>

      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
        <TextField
          label="Top"
          type="number"
          value={normalizedMargins.top}
          onChange={(e) =>
            setEffectiveMargins(
              dispatch,
              chartId,
              settings,
              { ...normalizedMargins, top: Number(e.target.value) },
              displayContext
            )
          }
          sx={{ width: 140 }}
        />

        <TextField
          label="Right"
          type="number"
          value={normalizedMargins.right}
          onChange={(e) =>
            setEffectiveMargins(
              dispatch,
              chartId,
              settings,
              { ...normalizedMargins, right: Number(e.target.value) },
              displayContext
            )
          }
          sx={{ width: 140 }}
        />

        <TextField
          label="Bottom"
          type="number"
          value={normalizedMargins.bottom}
          onChange={(e) =>
            setEffectiveMargins(
              dispatch,
              chartId,
              settings,
              { ...normalizedMargins, bottom: Number(e.target.value) },
              displayContext
            )
          }
          sx={{ width: 140 }}
        />

        <TextField
          label="Left"
          type="number"
          value={normalizedMargins.left}
          onChange={(e) =>
            setEffectiveMargins(
              dispatch,
              chartId,
              settings,
              { ...normalizedMargins, left: Number(e.target.value) },
              displayContext
            )
          }
          sx={{ width: 140 }}
        />

        {displayContext === 'leader' && leaderLayout && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const widthPercent = Math.max(
                  0.12,
                  Math.min(0.18, 40 / leaderLayout.chartAreaWidth)
                );

                const optimized = {
                  top: Math.min(15, Math.floor(leaderLayout.chartAreaHeight * 0.08)),
                  right: Math.min(15, Math.floor(leaderLayout.chartAreaWidth * 0.05)),
                  bottom: Math.min(35, Math.floor(leaderLayout.chartAreaHeight * 0.15)),
                  left: Math.min(
                    40,
                    Math.floor(leaderLayout.chartAreaWidth * widthPercent)
                  ),
                };

                setEffectiveMargins(
                  dispatch,
                  chartId,
                  settings,
                  optimized,
                  displayContext
                );
              }}
            >
              Auto-Optimize for Leader
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const minimal = { top: 10, right: 10, bottom: 25, left: 30 };
                setEffectiveMargins(
                  dispatch,
                  chartId,
                  settings,
                  minimal,
                  displayContext
                );
              }}
            >
              Minimal Margins
            </Button>
          </Box>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={(settings as any).enableEditRect !== false}
              onChange={(e) =>
                dispatch(
                  setSettings({
                    chartId,
                    settings: { enableEditRect: e.target.checked } as any,
                  })
                )
              }
            />
          }
          label="Show Canvas Rect on Dashboard Tile"
        />
      </Box>
    </Box>
  );
};

export default LayoutLegendControls;